import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import {
  Observable,
  switchMap,
  map,
  catchError,
  of,
  from,
  combineLatest,
  take
} from 'rxjs';
import * as fromPaymentsActions from '../actions/payments.actions';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import {
  AngularFirestore,
  Query,
  QueryFn
} from '@angular/fire/compat/firestore';
import {
  Payment,
  PaymentFirebaseResponse,
  PaymentsFilter
} from 'src/app/models/payments.model';
import { User } from 'src/app/models/user.model';

@Injectable()
export class PaymentsEffects {
  private readonly collectionName = 'payments';
  constructor(
    private readonly action$: Actions,
    private readonly firebaseService: FirebaseService,
    private readonly firestore: AngularFirestore
  ) {}

  // Load Payments
  loadPayments$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(fromPaymentsActions.LoadPaymentsAction),
      switchMap(
        ({
          trainer,
          filters
        }: {
          type: string;
          trainer: User;
          filters?: PaymentsFilter;
        }) =>
          this.getPaymentsForTrainer(trainer, filters).pipe(
            switchMap((payments) => this.addUsersToPayments(payments, trainer)),
            map((payments) => ({
              type: fromPaymentsActions.LOAD_PAYMENTS_SUCCESS,
              data: payments
            })),
            catchError((error) =>
              of({ type: fromPaymentsActions.LOAD_PAYMENTS_FAIL, error })
            )
          )
      )
    )
  );

  private getPaymentsForTrainer(
    trainer: User,
    filters?: PaymentsFilter
  ): Observable<Payment[]> {
    const queryFn: QueryFn = (ref) => {
      let query: Query = ref;
      if (filters?.state) {
        query = query.where('state', '==', filters?.state);
      }
      if (filters?.description) {
        const start = filters.description;
        const end = filters.description + '\uf8ff'; // `'\uf8ff'` es el último carácter en Unicode para cubrir todos los valores posibles

        query = query
          .where('description', '>=', start)
          .where('description', '<', end);
      }
      if (filters?.user) {
        const userRef = this.firestore.doc(`users/${filters.user}`).ref;
        query = query.where('userRef', '==', userRef);
      }
      const trainerRef = this.firestore.doc(`users/${trainer.id}`).ref;
      query = query.where('trainerRef', '==', trainerRef);

      return query;
    };

    return this.firebaseService
      .getDataOfCollectionByMultipleFields(this.collectionName, queryFn)
      .pipe(
        take(1),
        map((resp) =>
          resp.docs.map((doc) => ({
            ...(doc.data() as PaymentFirebaseResponse),
            id: doc.id
          }))
        )
      );
  }

  private addUsersToPayments(
    payments: Payment[],
    trainer: User
  ): Observable<Payment[]> {
    if (payments?.length > 0) {
      const userObservables = payments.map((payment) =>
        from(payment.userRef!.get())
      );
      return combineLatest(userObservables).pipe(
        map((users) =>
          payments.map((payment) => {
            const user: User = {
              ...users
                .find((userDoc) => userDoc.id === payment.userRef!.id)
                ?.data(),
              id: payment.userRef!.id
            } as any;
            delete payment.userRef;
            delete payment.trainerRef;
            delete user.trainerRef;
            delete user.teamRef;

            return {
              ...payment,
              trainer,
              user
            } as Payment;
          })
        )
      );
    }
    return of(payments);
  }
}
